import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Card,
  CardContent,
  alpha,
  Chip,
  Tooltip,
  Paper,
  LinearProgress,
  Stack,
  ButtonGroup,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Fade,
  Alert,
  Snackbar,
  Skeleton
} from '@mui/material';
import {
  Business as BusinessIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Receipt as ReceiptIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
  Spa as SpaIcon,
  MeetingRoom as MeetingRoomIcon,
  Wifi as WifiIcon,
  Tv as TvIcon,
  Phone as PhoneIcon,
  Restaurant as RestaurantIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useChains } from '../../contexts/ChainContext';
import { ThemeStyles } from '../../theme';
import useAnalytics from '../../hooks/useAnalytics';

const HotelDashboard = () => {
  const { user } = useAuth();
  const { isChainHQ } = useChains();
  const { trackPageView, trackUserAction, trackExport, trackFeatureUsage } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hotelStats, setHotelStats] = useState({
    active: 0,
    workInProgress: 0,
    inactive: 0,
    archived: 0,
    systems: {}
  });
  const [dashboardCategories, setDashboardCategories] = useState([]);
  const [viewFilter, setViewFilter] = useState('all'); // 'all', 'active', 'wip'
  const [allHotels, setAllHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Track page view on mount
  useEffect(() => {
    if (user) {
      trackPageView('/app/dashboard', {
        dashboard_view: 'overview',
        user_authenticated: true
      });
    }
  }, [user, trackPageView]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log('Fetching dashboard categories...');
        const categoriesRef = doc(db, 'categories', 'active_categories');
        const categoriesDoc = await getDoc(categoriesRef);
        
        if (categoriesDoc.exists()) {
          console.log('Categories document exists, processing data');
          const data = categoriesDoc.data();
          const categories = [];
          
          // Extract categories that should be shown on dashboard
          data.groups.forEach(group => {
            group.items.forEach(item => {
              // Check if showOnDashboard is true or not defined (for backward compatibility)
              if ((item.showOnDashboard === true || item.showOnDashboard === undefined) && !item.isArchived) {
                categories.push({
                  id: item.id,
                  fieldName: item.fieldName,
                  title: item.title,
                  groupTitle: group.title
                });
              }
            });
          });
          
          console.log(`Found ${categories.length} dashboard categories`);
          setDashboardCategories(categories);
        } else {
          console.log('Categories document does not exist');
          setDashboardCategories([]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    if (user) {
      console.log('User is available, fetching categories');
      fetchCategories();
    } else {
      console.log('No user available, skipping category fetch');
    }
  }, [user]);

  // Fetch hotel statistics
  const fetchHotelStats = useCallback(async (isManualRefresh = false) => {
    if (!user) {
      console.log('fetchHotelStats: No user available');
      return;
    }
    
    try {
      console.log('fetchHotelStats: Starting to fetch hotel stats');
      setLoading(true);
      setRefreshing(true);
      
      // Track refresh action
      if (isManualRefresh) {
        trackUserAction('dashboard_refresh', 'manual', {
          previous_filter: viewFilter
        });
      }
      
      // Get all hotels for the user
      const hotelsRef = collection(db, 'hotels');
      console.log('fetchHotelStats: User ID for query:', user.uid);
      const q = query(
        hotelsRef, 
        where('status', 'in', ['active', 'work_in_progress'])
      );
      console.log('fetchHotelStats: Query created, fetching hotels with updated filter logic');
      const querySnapshot = await getDocs(q);
      
      const hotels = [];
      console.log('fetchHotelStats: Processing query snapshot');
      querySnapshot.forEach((doc) => {
        hotels.push({ id: doc.id, ...doc.data() });
      });
      console.log(`fetchHotelStats: Found ${hotels.length} hotels before filtering out HQ chain hotels`);
      
      // Filter out hotels with HQ chains
      const filteredHotels = hotels.filter(hotel => {
        const chainValue = hotel.categoryData?.category_chain?.value;
        return !chainValue || !isChainHQ(chainValue);
      });
      console.log(`fetchHotelStats: ${hotels.length - filteredHotels.length} HQ chain hotels filtered out, ${filteredHotels.length} hotels remaining`);
      
      setAllHotels(filteredHotels);
      
      // Apply view filter if needed
      let viewFilteredHotels = [...filteredHotels];
      if (viewFilter === 'active') {
        viewFilteredHotels = filteredHotels.filter(hotel => hotel.status === 'active');
      } else if (viewFilter === 'work_in_progress') {
        viewFilteredHotels = filteredHotels.filter(hotel => hotel.status === 'work_in_progress');
      }
      console.log(`fetchHotelStats: Filtered to ${viewFilteredHotels.length} hotels with filter: ${viewFilter}`);
      
      setFilteredHotels(viewFilteredHotels);
      
      // Calculate statistics based on the filtered view
      const stats = {
        total: viewFilteredHotels.length,
        active: viewFilteredHotels.filter(hotel => hotel.status === 'active').length,
        workInProgress: viewFilteredHotels.filter(hotel => hotel.status === 'work_in_progress').length,
        systems: {}
      };
      
      // Initialize systems count for all dashboard categories
      dashboardCategories.forEach(category => {
        stats.systems[category.fieldName] = {};
      });
      
      // Count category values for each dashboard category using the filtered hotels
      console.log('fetchHotelStats: Processing category data for', dashboardCategories.length, 'dashboard categories');
      viewFilteredHotels.forEach(hotel => {
        // Log the first hotel's categoryData to help troubleshoot
        if (hotel === viewFilteredHotels[0]) {
          console.log('First hotel categoryData example:', hotel.categoryData);
        }
        
        dashboardCategories.forEach(category => {
          // Get the actual category key from the hotel data
          const categoryKey = `category_${category.fieldName.toLowerCase()}`;
          const value = hotel.categoryData?.[categoryKey]?.value;
          
          // Only process if we have a value
          if (value) {
            if (!stats.systems[category.fieldName][value]) {
              stats.systems[category.fieldName][value] = 0;
            }
            stats.systems[category.fieldName][value]++;
          }
        });
      });
      
      // Log final systems data for debugging
      console.log('fetchHotelStats: Final systems data:', stats.systems);
      
      setHotelStats(stats);
      console.log('fetchHotelStats: Completed successfully');
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching hotel stats:', error);
      // Check for permission errors which are common when changing query structure
      if (error.code === 'permission-denied') {
        console.error('Permission denied - You might need to update Firestore security rules to allow querying by status instead of userId');
        // Show in the UI
        setError({
          type: 'permission',
          message: 'Permission denied. Your Firestore security rules may need to be updated to allow the dashboard to query hotels by status.'
        });
      } else {
        setError({
          type: 'general',
          message: `Failed to load dashboard data: ${error.message}`
        });
      }
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [user, viewFilter, dashboardCategories, isChainHQ, trackUserAction]);

  useEffect(() => {
    console.log('Dashboard useEffect triggered with:', { 
      userAvailable: !!user, 
      categoriesLength: dashboardCategories.length 
    });
    
    if (user && dashboardCategories.length > 0) {
      console.log('Conditions met, calling fetchHotelStats');
      fetchHotelStats();
    } else if (user && dashboardCategories.length === 0) {
      // Set loading to false if there are no dashboard categories
      console.log('No dashboard categories found, setting loading to false');
      setLoading(false);
    } else {
      console.log('Conditions not met for fetchHotelStats');
    }
  }, [user, dashboardCategories, viewFilter, fetchHotelStats]);

  // Track filter changes
  const handleFilterChange = useCallback((newFilter) => {
    setViewFilter(newFilter);
    trackUserAction('dashboard_filter_changed', newFilter, {
      previous_filter: viewFilter,
      new_filter: newFilter,
      total_hotels: allHotels.length
    });
  }, [viewFilter, allHotels.length, trackUserAction]);

  // Get the icon for a system type
  const getSystemIcon = (system) => {
    const icons = {
      // Base system types
      pms: <ReceiptIcon />,
      pos: <DevicesIcon />,
      mice: <MeetingRoomIcon />,
      hr: <AdminPanelSettingsIcon />,
      spa: <SpaIcon />,
      cenium: <BusinessIcon />,
      cic: <SecurityIcon />,
      wifi: <WifiIcon />,
      tv: <TvIcon />,
      telephony: <PhoneIcon />,
      keycardSoftware: <SecurityIcon />,
      restaurantBooking: <RestaurantIcon />,
      
      // Additional common category types
      chain: <BusinessIcon />,
      brand: <BusinessIcon />,
      propertyNumber: <BusinessIcon />,
      propertyManagement: <AdminPanelSettingsIcon />,
      hotelManagement: <AdminPanelSettingsIcon />,
      website: <DevicesIcon />,
      email: <DevicesIcon />,
      booking: <ReceiptIcon />,
      reservation: <ReceiptIcon />
    };
    
    // Try to match by name if exact match not found
    const systemLower = system.toLowerCase();
    for (const [key, icon] of Object.entries(icons)) {
      if (systemLower.includes(key.toLowerCase())) {
        return icon;
      }
    }
    
    return <CategoryIcon />;
  };


  // Render a system card
  const SystemCard = ({ title, data, icon, color = 'primary', fieldName, onExpand }) => {
    const [expanded, setExpanded] = useState(false);
    
    // Track card expansion
    const handleExpand = useCallback((e) => {
      e?.stopPropagation?.();
      setExpanded(prev => {
        if (!prev && onExpand) {
          onExpand(title, fieldName, Object.keys(data).length);
        }
        return !prev;
      });
    }, [title, fieldName, data, onExpand]);
    
    // Get the total count of hotels using this system
    const totalCount = Object.values(data).reduce((sum, count) => sum + count, 0);
    
    // Get all systems sorted by usage
    const allSystems = Object.entries(data)
      .sort(([, countA], [, countB]) => countB - countA);
    
    // Check if this is a URL field (based on title or fieldName)
    const isUrlField = title.toLowerCase().includes('url') || 
                       title.toLowerCase().includes('website') || 
                       title.toLowerCase().includes('link');
    
    // If it's a URL field, group all URLs into a single entry
    let displayData = allSystems;
    if (isUrlField) {
      const urlCount = totalCount;
      displayData = [['URLs', urlCount]];
    }
    
    // Get the systems to display based on expanded state
    const displaySystems = expanded || isUrlField ? displayData : displayData.slice(0, 3);
    
    // Calculate percentages for progress bars (but not display)
    const calculatePercentage = (count) => {
      return totalCount > 0 ? (count / totalCount) * 100 : 0;
    };
    
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="caption">
              {totalCount} hotels using {Object.keys(data).length} different {Object.keys(data).length === 1 ? 'provider' : 'providers'}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 250,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
            }
          }
        }}
      >
        <Card 
          elevation={0}
          tabIndex={0}
          role="article"
          aria-label={`${title} system usage: ${totalCount} hotels using ${Object.keys(data).length} providers`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpand(e);
            }
          }}
          sx={{
            height: '100%',
            borderRadius: 4,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            bgcolor: 'background.default',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: theme => `
              0 4px 16px ${alpha(theme.palette.common.black, 0.06)},
              0 2px 8px ${alpha(theme.palette.common.black, 0.04)}
            `,
            '&:hover': {
              boxShadow: theme => `
                0 12px 32px ${alpha(theme.palette[color].main, 0.25)},
                0 6px 16px ${alpha(theme.palette[color].main, 0.18)},
                inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.2)}
              `,
              transform: 'translateY(-6px)'
            },
            '&:focus-visible': {
              outline: theme => `2px solid ${theme.palette[color].main}`,
              outlineOffset: 2,
              boxShadow: theme => `0 0 0 4px ${alpha(theme.palette[color].main, 0.2)}`
            }
          }}
        >
        <CardContent sx={{ p: { xs: 3, sm: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2.5, 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme => `linear-gradient(135deg, 
                  ${alpha(theme.palette[color].main, 0.18)} 0%, 
                  ${alpha(theme.palette[color].main, 0.1)} 100%
                )`,
                border: '1px solid',
                borderColor: theme => alpha(theme.palette[color].main, 0.25),
                color: `${color}.main`,
                mr: 2.5,
                boxShadow: theme => `
                  0 4px 12px ${alpha(theme.palette[color].main, 0.2)},
                  inset 0 1px 0 ${alpha(theme.palette.common.white, 0.25)}
                `,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'scale(1.1) rotate(5deg)',
                  boxShadow: theme => `
                    0 6px 16px ${alpha(theme.palette[color].main, 0.3)},
                    inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}
                  `
                }
              }}
            >
              {React.cloneElement(icon, {
                sx: { fontSize: 26 }
              })}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h5" 
                color="text.primary" 
                fontWeight={700}
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  mb: 0.5,
                  background: theme => `linear-gradient(135deg, 
                    ${theme.palette.text.primary} 0%, 
                    ${theme.palette[color].main} 100%
                  )`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.3
                }}
              >
                {title}
              </Typography>
              <Box sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: theme => alpha(theme.palette[color].main, 0.08),
                border: '1px solid',
                borderColor: theme => alpha(theme.palette[color].main, 0.15)
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontFamily: 'StrawberrySansText',
                    fontWeight: 500
                  }}
                >
                  <strong>{totalCount}</strong> hotels
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Divider 
            sx={{ 
              my: 3,
              borderColor: theme => alpha(theme.palette.divider, 0.1),
              borderWidth: '1px',
              '&::before, &::after': {
                borderColor: theme => alpha(theme.palette.divider, 0.1)
              }
            }} 
          />
          
          {displaySystems.length > 0 ? (
            <>
              <Stack spacing={2}>
                {displaySystems.map(([system, count]) => (
                  <Box key={system}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Tooltip title={isUrlField ? "Individual URLs are hidden for privacy" : `${system}`} arrow>
                        <Typography 
                          variant="body2" 
                          color="text.primary" 
                          noWrap 
                          sx={{ 
                            maxWidth: '70%',
                            fontWeight: 600,
                            fontFamily: 'StrawberrySansText'
                          }}
                        >
                          {system}
                        </Typography>
                      </Tooltip>
                      <Box sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        bgcolor: theme => alpha(theme.palette[color].main, 0.1),
                        border: '1px solid',
                        borderColor: theme => alpha(theme.palette[color].main, 0.2)
                      }}>
                        <Typography 
                          variant="body2" 
                          color={`${color}.main`} 
                          fontWeight={700}
                          sx={{ fontFamily: 'StrawberrySansDisplay' }}
                        >
                          {count}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={calculatePercentage(count) || 0}
                      sx={{
                        height: 10,
                        borderRadius: 2.5,
                        bgcolor: theme => alpha(theme.palette[color].main, 0.12),
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: theme => `inset 0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`,
                        '& .MuiLinearProgress-bar': {
                          background: theme => `linear-gradient(90deg, 
                            ${theme.palette[color].main} 0%, 
                            ${theme.palette[color].light || theme.palette[color].main} 100%
                          )`,
                          borderRadius: 2.5,
                          boxShadow: theme => `0 2px 6px ${alpha(theme.palette[color].main, 0.5)}`,
                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  </Box>
                ))}
              </Stack>
              
              {!isUrlField && allSystems.length > 3 && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button 
                    size="medium" 
                    onClick={handleExpand}
                    color={color}
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.875rem',
                      px: 3,
                      py: 1,
                      fontFamily: 'StrawberrySansText',
                      fontWeight: 500,
                      borderRadius: 2,
                      borderWidth: '1.5px',
                      bgcolor: theme => alpha(theme.palette[color].main, 0.05),
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: theme => `0 2px 8px ${alpha(theme.palette[color].main, 0.1)}`,
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette[color].main, 0.12),
                        transform: 'translateY(-2px)',
                        boxShadow: theme => `0 4px 12px ${alpha(theme.palette[color].main, 0.2)}`,
                        borderWidth: '2px'
                      }
                    }}
                    aria-label={expanded ? 'Collapse system list' : `Expand to show ${allSystems.length - 3} more systems`}
                  >
                    {expanded ? 'Show Less' : `Show More (${allSystems.length - 3})`}
                  </Button>
                </Box>
              )}
              
              {expanded && !isUrlField && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.3)}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      size="small" 
                      label={`${totalCount} hotels`} 
                      color={color} 
                      variant="outlined"
                    />
                    <Chip 
                      size="small" 
                      label={`${allSystems.length} providers`} 
                      color={color} 
                      variant="outlined"
                    />
                    <Chip 
                      size="small" 
                      label={`Top: ${allSystems[0]?.[0] || 'None'}`} 
                      color={color} 
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No data available
            </Typography>
          )}
        </CardContent>
      </Card>
      </Tooltip>
    );
  };

  // Generate a color for a category
  const getCategoryColor = (index) => {
    const colors = ['primary', 'secondary', 'info', 'success', 'warning', 'error'];
    return colors[index % colors.length];
  };


  // Skeleton loader components
  const SummaryCardSkeleton = ({ delay = 0 }) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        p: 3,
        mb: 4,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => `linear-gradient(
            90deg,
            ${alpha(theme.palette.background.paper, 0)} 0%,
            ${alpha(theme.palette.primary.main, 0.1)} 50%,
            ${alpha(theme.palette.background.paper, 0)} 100%
          )`,
          transform: 'translateX(-100%)',
          animation: 'shimmer 2s infinite',
          animationDelay: `${delay}ms`
        },
        '@keyframes shimmer': {
          '100%': {
            transform: 'translateX(100%)',
          },
        }
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5, bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
          <Skeleton variant="text" width="80%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
        </Box>
        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
      </Box>
      <Stack spacing={2}>
        <Box>
          <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
          <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
        </Box>
        <Box>
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
          <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
        </Box>
      </Stack>
    </Paper>
  );

  const StatCardSkeleton = ({ delay = 0 }) => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme => alpha(theme.palette.divider, 0.1),
        p: 3,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => `linear-gradient(
            90deg,
            ${alpha(theme.palette.background.paper, 0)} 0%,
            ${alpha(theme.palette.primary.main, 0.1)} 50%,
            ${alpha(theme.palette.background.paper, 0)} 100%
          )`,
          transform: 'translateX(-100%)',
          animation: 'shimmer 2s infinite',
          animationDelay: `${delay}ms`
        },
        '@keyframes shimmer': {
          '100%': {
            transform: 'translateX(100%)',
          },
        }
      }}
    >
      <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
      <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Skeleton variant="circular" width={16} height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
        <Skeleton variant="text" width="60%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
      </Box>
    </Card>
  );

  const SystemCardSkeleton = ({ delay = 0 }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme => alpha(theme.palette.divider, 0.1),
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => `linear-gradient(
            90deg,
            ${alpha(theme.palette.background.paper, 0)} 0%,
            ${alpha(theme.palette.primary.main, 0.1)} 50%,
            ${alpha(theme.palette.background.paper, 0)} 100%
          )`,
          transform: 'translateX(-100%)',
          animation: 'shimmer 2s infinite',
          animationDelay: `${delay}ms`
        },
        '@keyframes shimmer': {
          '100%': {
            transform: 'translateX(100%)',
          },
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1.5, mr: 2, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
            <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Box key={i}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Skeleton variant="text" width="60%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
                <Skeleton variant="text" width="20%" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  // Function to export dashboard data as CSV
  const exportDashboardData = useCallback(() => {
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Calculate operational total (only active + work in progress)
    const operationalTotal = hotelStats.active + hotelStats.workInProgress;
    
    // Add header row with notes
    csvContent += "# Hotel Dashboard Export\n";
    csvContent += "# Generated on: " + new Date().toLocaleString() + "\n";
    csvContent += "# Note: Headquarters (HQ) properties are excluded from active hotel counts\n\n";
    
    csvContent += "Category,Status,Count,Percentage\n";
    
    // Add hotel status data
    const activePercentage = operationalTotal > 0 ? Math.round((hotelStats.active / operationalTotal) * 100) : 0;
    const wipPercentage = operationalTotal > 0 ? Math.round((hotelStats.workInProgress / operationalTotal) * 100) : 0;
    
    csvContent += `Operational Hotels,Active,${hotelStats.active},${activePercentage}%\n`;
    csvContent += `Operational Hotels,Work In Progress,${hotelStats.workInProgress},${wipPercentage}%\n`;
    csvContent += `Operational Hotels,Total,${operationalTotal},100%\n\n`;
    
    // Add other hotel statuses (without percentages relative to operational hotels)
    csvContent += `Other Hotels,Inactive,${hotelStats.inactive}\n`;
    csvContent += `Other Hotels,Archived,${hotelStats.archived}\n`;
    csvContent += `All Hotels,Total,${allHotels.length}\n\n`;
    
    // Add filtered hotels count based on current view
    csvContent += `Filtered Hotels,${viewFilter === 'active' ? 'Active' : viewFilter === 'work_in_progress' ? 'Work In Progress' : 'All'},${filteredHotels.length}\n\n`;
    
    // Add system data
    csvContent += "System,Provider,Hotels,Percentage\n";
    
    // Process each system
    dashboardCategories.forEach(category => {
      const systemData = hotelStats.systems[category.fieldName] || {};
      const totalCount = Object.values(systemData).reduce((sum, count) => sum + count, 0);
      
      // Add each provider for this system
      Object.entries(systemData)
        .sort(([, countA], [, countB]) => countB - countA)
        .forEach(([provider, count]) => {
          const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
          csvContent += `${category.title},${provider},${count},${percentage}%\n`;
        });
        
      // Add empty line between systems
      if (Object.keys(systemData).length > 0) {
        csvContent += "\n";
      }
    });
    
    // Create download link and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `hotel_dashboard_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Track export action
    trackExport('csv', operationalTotal, {
      export_source: 'dashboard',
      active_hotels: hotelStats.active,
      wip_hotels: hotelStats.workInProgress,
      filter_applied: viewFilter,
      systems_count: dashboardCategories.length
    });
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Dashboard data exported successfully',
      severity: 'success'
    });
  }, [hotelStats, allHotels.length, viewFilter, dashboardCategories, filteredHotels.length, trackExport]);

  return (
    <Fade in={true}>
      <Box sx={{ 
        height: { xs: 'calc(100vh - 70px)', sm: 'calc(100vh - 85px)' }, // Account for header height
        overflowY: 'auto', // Make entire page scrollable
        bgcolor: 'background.default',
        ...ThemeStyles.scrollbar(theme),
        animation: 'fadeIn 0.3s ease-out',
        '@keyframes fadeIn': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}>
        <Container 
          maxWidth="xl" 
          sx={{ 
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 2, sm: 3, md: 4 },
            pb: { xs: 4, sm: 5, md: 6 }
          }}
        >
          {/* Modern Header Section */}
          <Paper 
            elevation={0} 
            sx={{ 
              mb: { xs: 3, sm: 4 },
              p: { xs: 3, sm: 4, md: 4 },
              borderRadius: 4,
              bgcolor: 'background.paper',
              boxShadow: theme => `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: { xs: 3, md: 4 },
              alignItems: { xs: 'stretch', md: 'center' }
            }}>
              {/* Left Section - Title and Description */}
              <Box sx={{ flex: { xs: 'none', md: '0 0 280px' } }}>
                <Typography 
                  variant="h4" 
                  component="h1"
                  sx={{
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1.5,
                    lineHeight: 1.3
                  }}
                >
                  Dashboard
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'StrawberrySansText',
                    fontWeight: 400,
                    lineHeight: 1.5
                  }}
                >
                  Monitor your hotel portfolio status and technology integrations
                </Typography>
              </Box>

              {/* Middle Section - Hotels Overview Stats */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {(() => {
                  const operationalTotal = hotelStats.active + hotelStats.workInProgress;
                  const activePercentage = operationalTotal > 0 ? Math.round((hotelStats.active / operationalTotal) * 100) : 0;
                  const wipPercentage = operationalTotal > 0 ? Math.round((hotelStats.workInProgress / operationalTotal) * 100) : 0;
                  
                  return (
                    <Box>
                      <Stack spacing={3}>
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: 'success.main'
                                }}
                              />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontFamily: 'StrawberrySansText', fontWeight: 500 }}
                              >
                                Active
                              </Typography>
                            </Box>
                            <Typography 
                              variant="h6" 
                              fontWeight={700} 
                              color="success.main"
                              sx={{ fontFamily: 'StrawberrySansDisplay' }}
                            >
                              {hotelStats.active}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={activePercentage || 0} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 1,
                              bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'success.main',
                                borderRadius: 1,
                                transition: 'transform 0.3s ease'
                              }
                            }} 
                          />
                        </Box>
                        
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: 'info.main'
                                }}
                              />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontFamily: 'StrawberrySansText', fontWeight: 500 }}
                              >
                                Work In Progress
                              </Typography>
                            </Box>
                            <Typography 
                              variant="h6" 
                              fontWeight={700} 
                              color="info.main"
                              sx={{ fontFamily: 'StrawberrySansDisplay' }}
                            >
                              {hotelStats.workInProgress}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={wipPercentage || 0} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 1,
                              bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'info.main',
                                borderRadius: 1,
                                transition: 'transform 0.3s ease'
                              }
                            }} 
                          />
                        </Box>
                      </Stack>
                    </Box>
                  );
                })()}
              </Box>

              {/* Right Section - Actions */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2, 
                alignItems: { xs: 'stretch', md: 'flex-end' },
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: '200px' }
              }}>
                  <ButtonGroup 
                    size={isMobile ? "small" : "medium"} 
                    disableElevation
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      width: '100%',
                      '& .MuiButton-root': {
                        fontFamily: 'StrawberrySansText',
                        fontWeight: 500,
                        flex: 1,
                        borderColor: 'divider',
                        '&.MuiButton-contained': {
                          borderColor: 'divider'
                        }
                      }
                    }}
                  >
                    <Tooltip title="Show all hotels" arrow>
                      <Button 
                        variant={viewFilter === 'all' ? 'contained' : 'outlined'} 
                        onClick={() => handleFilterChange('all')}
                        aria-label="Filter: All hotels"
                      >
                        All
                      </Button>
                    </Tooltip>
                    <Tooltip title="Show only active hotels" arrow>
                      <Button 
                        variant={viewFilter === 'active' ? 'contained' : 'outlined'} 
                        onClick={() => handleFilterChange('active')}
                        color="success"
                        aria-label="Filter: Active hotels"
                      >
                        Active
                      </Button>
                    </Tooltip>
                    <Tooltip title="Show only work in progress hotels" arrow>
                      <Button 
                        variant={viewFilter === 'work_in_progress' ? 'contained' : 'outlined'} 
                        onClick={() => handleFilterChange('work_in_progress')}
                        color="info"
                        aria-label="Filter: Work in progress hotels"
                      >
                        WIP
                      </Button>
                    </Tooltip>
                  </ButtonGroup>

                  <Tooltip 
                    title="Export dashboard data as CSV file"
                    arrow
                    placement="top"
                  >
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      color="primary"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportDashboardData}
                      disabled={loading || refreshing || error}
                      sx={{
                        fontFamily: 'StrawberrySansText',
                        fontWeight: 500,
                        borderRadius: 1.5,
                        width: '100%',
                        '&:hover:not(:disabled)': {
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      {isMobile ? "Export" : "Export Data"}
                    </Button>
                  </Tooltip>
              </Box>
            </Box>
        </Paper>

        {/* Enhanced Error State */}
        {error && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 4,
              borderRadius: 3,
              border: '1px dashed',
              borderColor: theme => alpha(theme.palette.error.main, 0.3),
              bgcolor: theme => alpha(theme.palette.error.main, 0.05),
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: theme => `radial-gradient(circle, ${alpha(theme.palette.error.main, 0.1)} 0%, transparent 70%)`,
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <Alert 
                severity="error" 
                icon={<InfoIcon />}
                sx={{ 
                  width: '100%',
                  mb: 2,
                  bgcolor: 'background.paper',
                  '& .MuiAlert-icon': {
                    fontSize: 28
                  }
                }}
              >
                <Typography 
                  variant="h6" 
                  color="error.main" 
                  gutterBottom
                  sx={{ fontFamily: 'StrawberrySansDisplay', mb: 1 }}
                >
                  Error Loading Dashboard Data
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'StrawberrySansText',
                    color: 'text.primary'
                  }}
                >
                  {error.message}
                </Typography>
              </Alert>
              
              {error.type === 'permission' && (
                <Alert 
                  severity="warning"
                  sx={{ 
                    width: '100%',
                    mb: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'StrawberrySansText',
                      color: 'text.primary'
                    }}
                  >
                    The dashboard now queries hotels by status instead of user ID. You may need to update your Firestore security rules to allow this type of query.
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setError(null);
                    fetchHotelStats(true);
                    trackUserAction('dashboard_error_retry', 'retry_clicked', {
                      error_type: error.type
                    });
                  }}
                  startIcon={<RefreshIcon />}
                  sx={{ 
                    fontFamily: 'StrawberrySansText',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                    }
                  }}
                >
                  Try Again
                </Button>
                {error.type === 'permission' && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      trackUserAction('dashboard_error_help', 'help_clicked', {
                        error_type: error.type
                      });
                      setSnackbar({
                        open: true,
                        message: 'Contact your administrator to update Firestore security rules.',
                        severity: 'info'
                      });
                    }}
                    sx={{ fontFamily: 'StrawberrySansText' }}
                  >
                    Get Help
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {loading ? (
          <Box>
            {/* Header Skeleton */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: theme => alpha(theme.palette.divider, 0.1),
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                  <Skeleton variant="text" width="300px" height={20} sx={{ mb: 0.5, bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
                  <Skeleton variant="text" width="150px" height={16} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.05) }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                  <Skeleton variant="rounded" width={200} height={36} sx={{ borderRadius: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                </Box>
              </Box>
            </Paper>

            {/* Summary Cards Skeleton */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <SummaryCardSkeleton delay={0} />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <StatCardSkeleton delay={100} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <StatCardSkeleton delay={200} />
                      </Grid>
                      <Grid item xs={12}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme => alpha(theme.palette.divider, 0.1),
                            p: 3,
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                              <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                            </Box>
                            <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 1.5, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* System Usage Skeleton */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Skeleton variant="text" width="150px" height={32} sx={{ mb: 3, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }} />
                  <Grid container spacing={3}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                        <SystemCardSkeleton delay={i * 100} />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <>
            {/* Enhanced Empty State */}
            {allHotels.length === 0 && (
              <Paper
                elevation={0}
                sx={{ 
                  mb: 3, 
                  p: 4,
                  border: '1px dashed', 
                  borderColor: theme => alpha(theme.palette.warning.main, 0.3),
                  borderRadius: 3,
                  bgcolor: theme => alpha(theme.palette.warning.main, 0.05),
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: theme => `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.1)} 0%, transparent 70%)`,
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <BusinessIcon 
                    sx={{ 
                      fontSize: 64, 
                      mb: 2, 
                      color: 'warning.main',
                      opacity: 0.6,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.6, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(1.05)' }
                      }
                    }} 
                  />
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      fontFamily: 'StrawberrySansDisplay',
                      fontWeight: 600,
                      color: 'warning.main',
                      mb: 1
                    }}
                  >
                    No Hotel Data Available
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'StrawberrySansText',
                      color: 'text.secondary',
                      mb: 3,
                      maxWidth: '500px',
                      mx: 'auto'
                    }}
                  >
                    We couldn't find any hotel data. This might be because:
                  </Typography>
                  <Box 
                    component="ul" 
                    sx={{ 
                      pl: 0,
                      mb: 3,
                      listStyle: 'none',
                      maxWidth: '400px',
                      mx: 'auto',
                      '& li': {
                        fontFamily: 'StrawberrySansText',
                        color: 'text.secondary',
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        '&::before': {
                          content: '"•"',
                          color: 'warning.main',
                          fontSize: '1.5rem',
                          lineHeight: 1
                        }
                      }
                    }}
                  >
                    <li>You have no hotels in the database</li>
                    <li>There's an issue with database connectivity</li>
                    <li>The categories configuration might be incorrect</li>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button 
                      variant="contained" 
                      color="warning" 
                      onClick={() => {
                        console.log('Manual refresh triggered');
                        fetchHotelStats(true);
                      }}
                      startIcon={<RefreshIcon />}
                      sx={{ 
                        fontFamily: 'StrawberrySansText',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme => `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
                        }
                      }}
                    >
                      Force Refresh Data
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="warning"
                      onClick={() => {
                        trackUserAction('dashboard_empty_state_help', 'help_clicked');
                        setSnackbar({
                          open: true,
                          message: 'Contact your administrator for assistance with hotel data setup.',
                          severity: 'info'
                        });
                      }}
                      sx={{ fontFamily: 'StrawberrySansText' }}
                    >
                      Get Help
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}
          
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* System Usage Section */}
              <Grid item xs={12}>
                {dashboardCategories.length > 0 ? (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: { xs: 3, sm: 4 },
                      borderRadius: 4,
                      background: theme => `linear-gradient(135deg, 
                        ${alpha(theme.palette.background.paper, 1)} 0%, 
                        ${alpha(theme.palette.background.paper, 0.98)} 100%
                      )`,
                      boxShadow: theme => `
                        0 4px 20px ${alpha(theme.palette.common.black, 0.06)},
                        0 2px 8px ${alpha(theme.palette.common.black, 0.04)},
                        inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.15)}
                      `,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      mb: { xs: 3, sm: 4 }, 
                      flexWrap: 'wrap', 
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Typography 
                        variant="h4" 
                        component="h2"
                        fontWeight={700} 
                        sx={{ 
                          fontFamily: 'StrawberrySansDisplay',
                          color: 'text.primary',
                          lineHeight: 1.2
                        }}
                      >
                        System Usage
                      </Typography>
                      
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              System Usage Overview
                            </Typography>
                            <Typography variant="caption">
                              Shows the distribution of systems used by hotels. Each card displays the number of hotels using each system provider.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 300,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
                            }
                          },
                          arrow: {
                            sx: {
                              color: 'background.paper',
                              '&::before': {
                                border: '1px solid',
                                borderColor: 'divider'
                              }
                            }
                          }
                        }}
                      >
                        <IconButton 
                          size="small"
                          onClick={() => {
                            trackFeatureUsage('dashboard_info_click', {
                              info_type: 'system_usage',
                              systems_count: dashboardCategories.length
                            });
                          }}
                          sx={{
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.1)'
                            }
                          }}
                          aria-label="System usage information"
                        >
                          <InfoIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Grid container spacing={{ xs: 2.5, sm: 3, md: 3.5 }}>
                      {dashboardCategories.map((category, index) => (
                        <Grid 
                          item 
                          xs={12} 
                          sm={dashboardCategories.length <= 2 ? 12 : 6} 
                          md={dashboardCategories.length <= 4 ? 6 : 4} 
                          lg={dashboardCategories.length <= 3 ? 4 : 3} 
                          key={category.id}
                          sx={{
                            transition: 'all 0.3s ease',
                            animation: `fadeInUp 0.6s ease-out ${index * 0.08}s both`,
                            '@keyframes fadeInUp': {
                              from: {
                                opacity: 0,
                                transform: 'translateY(24px)'
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateY(0)'
                              }
                            },
                            // Mobile-specific optimizations
                            ...(isMobile && {
                              '&:active': {
                                transform: 'scale(0.98)'
                              }
                            })
                          }}
                        >
                          <SystemCard 
                            title={`${category.title}`}
                            data={hotelStats.systems[category.fieldName] || {}}
                            icon={getSystemIcon(category.fieldName)}
                            color={getCategoryColor(index)}
                            fieldName={category.fieldName}
                            onExpand={(title, fieldName, providerCount) => {
                              trackFeatureUsage('dashboard_system_card_expand', {
                                system_name: title,
                                system_field: fieldName,
                                total_providers: providerCount
                              });
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                ) : (
                  <Paper 
                    elevation={0} 
                    sx={{
                      p: 4, 
                      textAlign: 'center', 
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: theme => alpha(theme.palette.divider, 0.1),
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontFamily: 'StrawberrySansDisplay' }}
                    >
                      No Dashboard Categories Configured
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontFamily: 'StrawberrySansText' }}
                    >
                      Use the Category Manager to mark categories for display on the dashboard
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </>
        )}
        </Container>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={Fade}
          sx={ThemeStyles.snackbar(theme)}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ 
              position: 'relative',
              zIndex: 2,
              '& .MuiAlert-message': {
                fontFamily: 'StrawberrySansText',
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default HotelDashboard; 